// Range/206 support (browserdav-transport-robustness). Reference behavior
// is Nephele-as-shipped (GET_HEAD.js + range-parser {combine:true}):
//   - GET only (HEAD ignores Range but still advertises Accept-Ranges)
//   - If-Range: weak etag → 400; quoted etag mismatch → full 200;
//     validator date older than Last-Modified → full 200
//   - malformed Range → 400; unsatisfiable → 416 (with Content-Range */size)
//   - one range → 206 + Content-Range; several → multipart/byteranges
// Slices are file.slice() — lazy, no bytes copied until the transport reads
// them, so a 206 on a huge file costs one slice descriptor.

/**
 * Parse a Range header against a resource size, range-parser compatible:
 * returns -2 for a malformed header (no '='), -1 when no requested range is
 * satisfiable, else a sorted array of combined `{start, end}` (inclusive).
 * Matching the reference (range-parser as Nephele uses it): the unit is not
 * validated, and unparseable specs are skipped rather than rejected — a
 * header full of garbage after '=' is *unsatisfiable* (416), not malformed
 * (400).
 */
export function parseRangeHeader(header, size) {
    if (typeof header !== 'string') return -2
    const eq = header.indexOf('=')
    if (eq === -1) return -2
    const specs = header
        .slice(eq + 1)
        .split(',')
        .map((s) => s.trim())

    const ranges = []
    for (const spec of specs) {
        const m = spec.match(/^(\d*)-(\d*)$/)
        if (!m) continue
        const [, startStr, endStr] = m
        if (startStr === '' && endStr === '') continue
        let start
        let end
        if (startStr === '') {
            // suffix form: last N bytes
            const n = parseInt(endStr, 10)
            if (n === 0) continue // zero-length suffix satisfies nothing
            start = Math.max(0, size - n)
            end = size - 1
        } else {
            start = parseInt(startStr, 10)
            end = endStr === '' ? size - 1 : Math.min(parseInt(endStr, 10), size - 1)
        }
        if (start > end || start >= size) continue // this spec is unsatisfiable
        ranges.push({ start, end })
    }
    if (ranges.length === 0) return -1

    ranges.sort((a, b) => a.start - b.start)
    const combined = [ranges[0]]
    for (const r of ranges.slice(1)) {
        const last = combined[combined.length - 1]
        if (r.start <= last.end + 1) last.end = Math.max(last.end, r.end)
        else combined.push(r)
    }
    return combined
}

/**
 * Decide whether a GET should serve partial content.
 * Returns the combined ranges to serve, or null for a full-body response.
 * Throws `{status: 400|416, message, headers?}` on protocol errors.
 *
 * @param {object} p
 * @param {string} [p.rangeHeader] - Range header value
 * @param {string} [p.ifRange] - If-Range header value
 * @param {string} p.etag - resource ETag, unquoted
 * @param {number} p.lastModified - resource mtime (ms epoch)
 * @param {number} p.size - resource size in bytes
 */
export function evaluateRange({ rangeHeader, ifRange, etag, lastModified, size }) {
    if (rangeHeader == null) return null

    if (ifRange != null) {
        const v = ifRange.trim()
        if (v.startsWith('W/')) {
            throw { status: 400, message: 'If-Range must not contain a weak etag.' }
        }
        if (v.startsWith('"') || v.startsWith("'")) {
            if (v.replace(/^["']/, '').replace(/["']$/, '') !== etag) return null
        } else if (Math.floor(new Date(v).getTime() / 1000) < Math.floor(new Date(lastModified).getTime() / 1000)) {
            // HTTP dates carry whole seconds; the file's mtime carries
            // milliseconds. Compare at the header's resolution, or a
            // Last-Modified the server itself sent never matches (found
            // 2026-10-19 by the pull's Range resume).
            return null
        }
    }

    const ranges = parseRangeHeader(rangeHeader, size)
    if (ranges === -2) {
        throw { status: 400, message: 'The given Range header is not valid.' }
    }
    if (ranges === -1) {
        throw {
            status: 416,
            message: 'The specified Range header is not satisfiable.',
            headers: { 'Content-Range': `bytes */${size}` },
        }
    }
    return ranges
}

/**
 * Build the 206 response content for `ranges` of `file` (a File/Blob).
 * Returns `{ status, file, mimeType, headers }` in FileOperations.GET's
 * content shape; the dispatch layer forwards status and headers.
 */
export function buildRangeResponse(file, ranges, mimeType, validatorHeaders) {
    if (ranges.length === 1) {
        const { start, end } = ranges[0]
        const slice = file.slice(start, end + 1, mimeType)
        return {
            status: 206,
            file: slice,
            mimeType,
            headers: {
                ...validatorHeaders,
                'Content-Length': String(slice.size),
                'Content-Range': `bytes ${start}-${end}/${file.size}`,
            },
        }
    }

    // multipart/byteranges — RFC 7233 framing (CRLF, closing --boundary--).
    // Nephele's own framing is looser (bare LF, no terminator); the
    // conformance cases compare structurally, not byte-for-byte, since the
    // boundary is random on both servers anyway.
    const boundary = crypto.randomUUID()
    const parts = []
    for (const { start, end } of ranges) {
        parts.push(
            `--${boundary}\r\nContent-Type: ${mimeType}\r\nContent-Range: bytes ${start}-${end}/${file.size}\r\n\r\n`
        )
        parts.push(file.slice(start, end + 1))
        parts.push('\r\n')
    }
    parts.push(`--${boundary}--`)
    const body = new Blob(parts)
    const multipartType = `multipart/byteranges; boundary=${boundary}`
    return {
        status: 206,
        file: body,
        mimeType: multipartType,
        headers: {
            ...validatorHeaders,
            'Content-Length': String(body.size),
        },
    }
}